/**
 * The selected and the hovered cell drawn on the ground: the two marks on the
 * leaves, the cell the selection mark stands on, and the selection and its
 * reference footprint pushed into the plot.
 */

import type * as THREE from 'three';

import type { AtlasLive } from '../atlas';
import { arrayAreaFor, type PlotClimate, type SubPlot } from './plotField.ts';
import type { PyramidViewHandle } from './pyramidView.ts';
import { createSubPlotMark, type SubPlotMarkHandle } from './subPlotMark.ts';
import type { InjectsHandle } from './injects.ts';
import type { PlotMountHandle } from './plotMount.ts';
import type { SolarScrubHandle } from './solarScrub.ts';

export interface SelectionDeps {
  own(fn: () => void): void;
  readonly tileGroup: THREE.Group;
  readonly plotMount: PlotMountHandle;
  readonly solarScrub: SolarScrubHandle;
  referenceWhSol(climate: PlotClimate): number;
  closeUp(): boolean;
  plotGroundLocal(xM: number, zM: number, out: THREE.Vector3): THREE.Vector3 | null;
  refreshStructures(): void;
  syncBuildButton(cell?: string | null): void;
  readonly pyramidView: PyramidViewHandle;
  readonly injects: InjectsHandle;
  readonly live: AtlasLive;
}

/** The marks' own state that the tick reads and writes. */
export interface SelectionState {
  markHeightVersion: number;
  markExag: number;
}

export interface SelectionHandle {
  readonly selectMark: SubPlotMarkHandle;
  readonly hoverMark: SubPlotMarkHandle;
  selectionDrawn(): boolean;
  syncMarks(): void;
  applySelection(): void;
  readonly state: SelectionState;
}

export function createSelection(deps: SelectionDeps): SelectionHandle {
  const {
    own, tileGroup, referenceWhSol, closeUp, plotGroundLocal, refreshStructures, syncBuildButton,
  } = deps;
  const { dtmGroup } = deps.plotMount;
  const { solar } = deps.solarScrub;

  // The selection and the cursor's own answer, drawn on the leaves. Children of
  // the tile group because that is the frame the leaves are in; hidden inside
  // the walkaround, where the plot mesh's own shader draws the same two marks.
  const selectMark = createSubPlotMark({
    groundAt: (xM, zM, out) => plotGroundLocal(xM, zM, out),
    colour: [1.0, 0.95, 0.74],
    gain: 1,
    pulse: true,
  });
  const hoverMark = createSubPlotMark({
    groundAt: (xM, zM, out) => plotGroundLocal(xM, zM, out),
    colour: [0.78, 0.89, 0.91],
    gain: 0.45,
    pulse: false,
  });
  tileGroup.add(selectMark.object);
  tileGroup.add(hoverMark.object);
  own(() => {
    tileGroup.remove(selectMark.object);
    tileGroup.remove(hoverMark.object);
    selectMark.dispose();
    hoverMark.dispose();
  });
  /** The height version the two marks were last laid on. */
  let markHeightVersion = -1;
  /** And the relief exaggeration, which moves the same vertices. */
  let markExag = 0;

  /**
   * The cell the GROUND marks, which is the selection unless a control moved it.
   *
   * `--select`'s two injections both live here rather than at the pick, so the
   * panel goes on naming the cell that was clicked while the mark goes missing
   * or lands next door - which is the pair of failures the arm's draw bar has
   * to tell apart from a working mark.
   */
  function drawnSub(): SubPlot | null {
    if (!deps.live.selectedSub || deps.injects.state.selectDrawOff) return null;
    if (!deps.injects.state.selectWrongCell) return deps.live.selectedSub;
    const me = deps.live.selectedSub;
    let near: SubPlot | null = null;
    let best = Infinity;
    for (const s of deps.live.plotSubs) {
      if (s.id === me.id) continue;
      const d = Math.hypot(s.x - me.x, s.z - me.z);
      if (d < best) { best = d; near = s; }
    }
    return near;
  }

  /** Whether the selected cell is marked on the GROUND, not only in the panel. */
  function selectionDrawn(): boolean {
    if (drawnSub() === null) return false;
    return selectMark.drawn || (deps.live.plot !== null && dtmGroup.visible);
  }

  /** Lay both ground marks on the cells they answer for, in the leaves' frame. */
  function syncMarks(): void {
    const shown = drawnSub();
    // Never both: the cursor resting on the cell it already picked would draw
    // the wash twice and read as a different, brighter state.
    const under = deps.live.hoverSub && deps.live.hoverSub.id !== shown?.id && !deps.injects.state.selectDrawOff ? deps.live.hoverSub : null;
    if (selectMark.set(shown) || hoverMark.set(under)) deps.live.needsRender = true;
    markHeightVersion = deps.pyramidView.tileHeightVersion;
    markExag = solar.exag;
  }

  /** Push the current selection, and the reference footprint it implies. */
  function applySelection(): void {
    deps.live.needsRender = true;
    syncMarks();
    if (!deps.live.plot) return;
    const shown = drawnSub();
    deps.live.plot.setSelection(shown);
    // Selecting a cell now OUTLINES what an array would take, and builds
    // nothing. Surveying is a question and building is an assertion, and
    // running the first over fifty cells should not litter the claim with
    // hardware as you read it.
    //
    // The outline still fades as the camera drops, for the same reason the
    // verdict wash does: an annotation drawn on the world is information only
    // while you are reading the world as a map.
    deps.live.plot.setReferenceFootprint(
      closeUp() ? null : shown,
      shown ? arrayAreaFor(referenceWhSol(deps.live.plot.climate), shown.insolationWhSol) : 0,
    );
    refreshStructures();
    syncBuildButton(undefined);
  }

  return {
    selectMark, hoverMark, selectionDrawn, syncMarks, applySelection,
    state: {
      get markHeightVersion() { return markHeightVersion; },
      set markHeightVersion(v) { markHeightVersion = v; },
      get markExag() { return markExag; },
      set markExag(v) { markExag = v; },
    },
  };
}
